import React, { FC, useEffect, useState } from 'react';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import styles from './PortfolioManagersTable.module.scss';
import { PortfolioModel } from 'pages/PortfoliosPage/PortfolioModel';
import { BaseUserModel } from 'models/BaseUserModel';
import { UserService } from 'pages/UserListPage/UserService';
import { PortfolioService } from 'pages/PortfoliosPage/PortfolioService';
import AutocompleteAssociationsTable from 'components/AutocompleteAssociationsTable/AutocompleteAssociationsTable';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';

interface PortfolioManagersTableProps {
  portfolio: PortfolioModel;
  isLoading: boolean;
  onPortfolioUpdate: (portfolioId: string) => void;
}

const PortfolioManagersTable: FC<PortfolioManagersTableProps> = (props: PortfolioManagersTableProps) => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [displayedManagers, setDisplayedManagers] = useState<{id: string, email: string}[]>([]);
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [unassociatedUsers, setUnassociatedUsers] = useState<BaseUserModel[]>([]);
  const [users, setUsers] = useState<BaseUserModel[]>([])

  useEffect(() => {
    const getUsers = async (): Promise<void> => {
      try {
        const fetchedUsers: BaseUserModel[] = await UserService.getUsers();

        if (!fetchedUsers) {
          throw new Error('No users returned by the service.');
        }

        setUsers(fetchedUsers);
      } catch (error) {
        console.error('Failed at getting users:', error);
        setUsers([]);
        const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting users. Please refresh, and contact support if the issue persists.'};
        setCurrentAlert(alertConfig);
        setIsAlertDialogOpen(true);
      }
    };

    getUsers();

    const managers = Object.keys(props.portfolio?.managers || {}).map((email: string, index: number) => ({
      id: index.toString(),
      email: email
    }));

    setDisplayedManagers(managers);
  }, [props.portfolio]);

  useEffect(() => {
    if (!users || !displayedManagers) return;

      const managerEmails = displayedManagers.map(manager => manager.email);
      const unassociatedUsers = users.filter(user => !managerEmails.includes(user.email));

      setUnassociatedUsers(unassociatedUsers);
  }, [displayedManagers, users]);

  const addManagerHandler = async (event: any, selectedUser: any): Promise<void> => {
    if (!selectedUser) return;

    try {
      const addManagersResult = await PortfolioService.addManagers(props.portfolio._id, [selectedUser.email]);

      if (!addManagersResult) {
        throw new Error('Error adding portfolio manager.');
      }

      props.onPortfolioUpdate(props.portfolio._id);
    } catch (error) {
      console.error('Failed at adding portfolio manager');
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at adding portfolio manager. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }

  };

  const removeManagerHandler = async (managerEmail: string): Promise<void> => {
    if (!managerEmail) return;

    try {
      const removeManagersResult = await PortfolioService.removeManagers(props.portfolio._id, [managerEmail])

      if (!removeManagersResult) {
        throw new Error('Error removing portfolio manager');
      }

      props.onPortfolioUpdate(props.portfolio._id);
    } catch (error) {
      console.error('Failed at removing portfolio manager');
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at removing portfolio manager. Please try again, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    }
  };

  const renderManagerActions = (params: GridRenderCellParams): JSX.Element => {
    return (
      <IconButton
        color="error"
        onClick={() => removeManagerHandler(params.row.email)}
      >
        <DeleteIcon />
      </IconButton>
    )
  }

  const portfolioManagerColumns: GridColDef[] = [
    { field: 'email', headerName: 'Email', flex: 2 },
    {
      field: 'actions',
      headerName: '',
      width: 40,
      renderCell: renderManagerActions,
    },
  ];

  return (
    <div className={styles.PortfolioManagersTable} data-testid="PortfolioManagersTable">
      <AutocompleteAssociationsTable
        displayedItems={displayedManagers}
        unassociatedItems={unassociatedUsers}
        columns={portfolioManagerColumns}
        isLoading={props.isLoading}
        onAddItem={addManagerHandler}
        getOptionLabel={(option) => option.email}
        title="Managers"
      />

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </div>
  );
}

export default PortfolioManagersTable;
